import Cookies from 'js-cookie';

type TrackingProvider =
  'meta_pixel' | 'google_tag_manager' | 'google_analytics';

type SpaceTrackingConfig = {
  provider: TrackingProvider;
  account_id?: string;
};

type TrackingProperties = Record<string, unknown>;

type ProviderDefinition = {
  consentCookie: string;
  load: (accountId: string) => void;
  track: (
    eventName: string,
    properties: TrackingProperties,
    accountId?: string,
  ) => void;
};

type CobotTrackingApi = {
  track: (eventName: string, properties?: TrackingProperties) => void;
  ready: boolean;
};

declare global {
  interface Window {
    CobotTracking?: CobotTrackingApi;
    dataLayer?: unknown[];
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
    gtag?: (...args: unknown[]) => void;
  }
}

const queuedEvents: Array<{ name: string; properties: TrackingProperties }> =
  [];
let providerReady = false;
let providerLoading = false;

function resetRuntimeState(): void {
  queuedEvents.length = 0;
  providerReady = false;
  providerLoading = false;
}

function readConfig(): SpaceTrackingConfig | null {
  const el = document.getElementById('cobot-space-tracking-config');
  if (!el?.textContent?.trim()) return null;
  try {
    return JSON.parse(el.textContent) as SpaceTrackingConfig;
  } catch (e) {
    console.error('Invalid Cobot space tracking config', e);
    return null;
  }
}

function consentRequired(): boolean {
  const meta = document.querySelector(
    'meta[name="cobot:require-tracking-consent"]',
  );
  return meta?.getAttribute('content') === 'true';
}

function ensureDataLayer(): unknown[] {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

function loadMetaPixel(pixelId: string): void {
  if (window.fbq) return;
  const fbq: ((...args: unknown[]) => void) & {
    queue?: unknown[];
    loaded?: boolean;
    version?: string;
    callMethod?: (...args: unknown[]) => void;
  } = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue = fbq.queue || [];
      fbq.queue.push(args);
    }
  };
  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = '2.0';
  window.fbq = fbq;
  window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
}

function loadGtm(containerId: string): void {
  const dataLayer = ensureDataLayer();
  dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  document.head.appendChild(script);
}

function loadGoogleAnalytics(measurementId: string): void {
  ensureDataLayer();
  window.gtag =
    window.gtag ||
    function (...args: unknown[]) {
      ensureDataLayer().push(args);
    };
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
  window.gtag('js', new Date());
  window.gtag('config', measurementId);
  window.gtag('event', 'page_view', { send_to: measurementId });
}

function trackMetaPixel(
  eventName: string,
  properties: TrackingProperties,
): void {
  window.fbq?.('trackCustom', eventName, properties);
  if (eventName === 'membership_signup_completed') {
    window.fbq?.('track', 'CompleteRegistration', properties);
  } else if (eventName === 'time_pass_purchase_completed') {
    window.fbq?.('track', 'Purchase', {
      ...properties,
      value: properties.price,
      currency: properties.currency,
    });
  }
}

function trackGtm(eventName: string, properties: TrackingProperties): void {
  ensureDataLayer().push({ ...properties, event: `cobot.${eventName}` });
}

function trackGoogleAnalytics(
  eventName: string,
  properties: TrackingProperties,
  accountId?: string,
): void {
  window.gtag?.('event', eventName, {
    ...properties,
    send_to: accountId,
  });
}

const providers: Record<TrackingProvider, ProviderDefinition> = {
  meta_pixel: {
    consentCookie: 'ad_storage',
    load: loadMetaPixel,
    track: trackMetaPixel,
  },
  google_tag_manager: {
    consentCookie: 'analytics_storage',
    load: loadGtm,
    track: trackGtm,
  },
  google_analytics: {
    consentCookie: 'analytics_storage',
    load: loadGoogleAnalytics,
    track: trackGoogleAnalytics,
  },
};

function consentGranted(provider: TrackingProvider): boolean {
  return Cookies.get(providers[provider].consentCookie) === 'granted';
}

function consentDenied(provider: TrackingProvider): boolean {
  return Cookies.get(providers[provider].consentCookie) === 'denied';
}

function canLoadProvider(provider: TrackingProvider): boolean {
  if (consentGranted(provider)) return true;
  if (consentDenied(provider)) return false;
  if (Cookies.get('cookie_consent_set') === 'true') return false;
  return !consentRequired();
}

function dispatchCobotEvent(
  eventName: string,
  properties: TrackingProperties,
): void {
  document.dispatchEvent(
    new CustomEvent('cobot:tracking', {
      detail: { name: eventName, properties },
    }),
  );
}

function forwardToProvider(
  config: SpaceTrackingConfig,
  eventName: string,
  properties: TrackingProperties,
): void {
  providers[config.provider].track(eventName, properties, config.account_id);
  dispatchCobotEvent(eventName, properties);
}

function flushQueue(config: SpaceTrackingConfig): void {
  while (queuedEvents.length > 0) {
    const event = queuedEvents.shift();
    if (!event) break;
    forwardToProvider(config, event.name, event.properties);
  }
}

async function activateProvider(config: SpaceTrackingConfig): Promise<void> {
  if (providerReady || providerLoading) return;
  if (!canLoadProvider(config.provider)) return;

  providerLoading = true;
  if (config.account_id) {
    providers[config.provider].load(config.account_id);
  }
  providerReady = true;
  providerLoading = false;
  if (window.CobotTracking) window.CobotTracking.ready = true;
  flushQueue(config);
}

function track(
  config: SpaceTrackingConfig | null,
  eventName: string,
  properties: TrackingProperties = {},
): void {
  if (!config) {
    dispatchCobotEvent(eventName, properties);
    return;
  }
  if (!providerReady) {
    queuedEvents.push({ name: eventName, properties });
    void activateProvider(config);
    return;
  }
  forwardToProvider(config, eventName, properties);
}

function parseProps(raw: string | undefined): TrackingProperties {
  if (!raw) return {};
  try {
    return JSON.parse(raw) as TrackingProperties;
  } catch {
    return {};
  }
}

function bindDeclarativeEvents(config: SpaceTrackingConfig | null): void {
  document
    .querySelectorAll<HTMLElement>('[data-cobot-track-event]')
    .forEach((el) => {
      const name = el.dataset.cobotTrackEvent;
      if (!name) return;
      const properties = parseProps(el.dataset.cobotTrackProps);
      const on = el.dataset.cobotTrackOn || 'load';
      if (on === 'load') {
        track(config, name, properties);
      }
    });

  document
    .querySelectorAll<HTMLElement>('[data-cobot-track-click]')
    .forEach((el) => {
      el.addEventListener('click', () => {
        const name = el.dataset.cobotTrackClick;
        if (!name) return;
        track(config, name, parseProps(el.dataset.cobotTrackProps));
      });
    });

  document
    .querySelectorAll<HTMLFormElement>('[data-cobot-track-submit]')
    .forEach((form) => {
      form.addEventListener('submit', () => {
        const name = form.dataset.cobotTrackSubmit;
        if (!name) return;
        track(config, name, parseProps(form.dataset.cobotTrackProps));
      });
    });
}

export default function initSpaceTracking(): void {
  resetRuntimeState();
  const config = readConfig();

  window.CobotTracking = {
    track: (eventName, properties = {}) => track(config, eventName, properties),
    ready: !config,
  };

  if (config) {
    void activateProvider(config);
    document.addEventListener('cobot:consent-updated', () => {
      void activateProvider(config);
    });
  }

  bindDeclarativeEvents(config);
}
